<template>
  <div>
    <div class="c-tabs">
      <div class="c-tabs-header">
        <template v-if="!isEditing">
          <span
            v-for="(tab, index) in tabsPlaceholderData"
            :key="`tab${index}`"
            :class="`tab-nav-item ${index === activeTabIndex ? 'active' : ''}`"
          >
            <button class="tab-nav-link" v-on:click="activeTabIndex = index">
              <sc-text :field="tab.$props.fields?.name" />
            </button>
          </span>
        </template>
      </div>
      <div :class="{ 'c-tabs-content': !isEditing }">
        <template v-if="isEditing">
          <component v-for="(tab, index) in tabsPlaceholderData" :is="tab" :key="`tab${index}`" />
        </template>
        <template v-else>
          <component :is="activeTab" />
        </template>
      </div>
    </div>

    <!-- Mobile - Tab Accordion -->
    <div v-if="isMobile" class="c-tabs-mobile">
      <template v-if="!isEditing">
        <div
          v-for="(tab, index) in tabsPlaceholderData"
          :key="`acc${index}`"
          :class="{ active: isOpen.includes(index) }"
        >
          <div
            class="c-tabs-mobile__title"
            @click="
              toggle(index);
              restoreWistia($event);
            "
          >
            <sc-text :field="tab.$props.fields?.name" />
          </div>
          <div class="c-tabs-mobile__content">
            <component :is="tab" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, markRaw } from 'vue';
import { Text, File } from '@sitecore-jss/sitecore-jss-vue';
import findParentComponentByName from '../../lib/findParentComponentByName';

export default {
  name: 'CIS-Tabs',
  props: {
    fields: {
      type: Object
    },
    rendering: {
      type: Object
    }
  },
  components: {
    ScText: Text,
    ScFile: File
  },
  data() {
    return {
      activeTabIndex: 0,
      isOpen: [],
      tabsPlaceholderData: markRaw(this.$options.computed.tabsPlaceholder)
    };
  },
  setup() {
    const isMobile = ref(window.innerWidth <= 768);
    let wistiaLoaded = false;

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    onMounted(() => {
      window.addEventListener('resize', checkMobile);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile);
    });

    return { isMobile, wistiaLoaded };
  },
  withPlaceholder() {
    return {
      placeholders: [
        {
          placeholder: 'jss-tabs',
          computedPropName: 'tabsPlaceholder'
        }
      ]
    };
  },
  updated() {
    findParentComponentByName(this, 'Layout')?.externalLinks();
  },
  computed: {
    isEditing() {
      return this.$jss.sitecoreContext().pageEditing;
    },
    activeTab() {
      const tab = this.tabsPlaceholderData && this.tabsPlaceholderData[this.activeTabIndex];
      return tab;
    }
  },
  methods: {
    toggle(index, event) {
      if (this.isOpen.includes(index)) {
        this.isOpen = this.isOpen.filter((i) => i !== index);
        return;
      }

      this.isOpen.push(index);
    },
    restoreWistia(event) {
      const wistiaChannel = event.target.nextElementSibling.querySelector('.wistia-channel');
      if (wistiaChannel) {
        wistiaChannel.innerHTML = '';
        this.wistiaLoaded = false;
        const wistiaData = wistiaChannel.dataset.wistia;
        if (!this.wistiaLoaded && wistiaData) {
          this.wistiaLoaded = true;
          const wistiaDiv = document.createElement('div');
          wistiaDiv.className = `wistia_channel wistia_async_${wistiaData} mode=inline`;
          wistiaChannel.appendChild(wistiaDiv);
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.c-tabs {
  display: none;
  margin-bottom: 4rem;

  @include tablet-up {
    display: block;
  }

  &-header {
    display: flex;

    .tab-nav-item {
      display: flex;
      justify-content: center;
      width: 100%;
      margin-right: 1.1rem;
      box-shadow: 0 -2px 5px 0 rgba(0, 0, 0, 0.02), 0 18px 17px 0 rgba(0, 0, 0, 0.08),
        0 2px 4px 0 rgba(0, 0, 0, 0.08);
      @include font-styles('Arial', 2rem, 300, normal, 2.5rem, 0, $blue);

      &.active {
        color: $white;
        background: linear-gradient(
          169deg,
          rgba(0, 0, 0, 1) 0%,
          rgba(0, 59, 92, 1) 20%,
          rgba(0, 134, 191, 1) 100%
        );
      }

      &:last-child {
        margin-right: 0;
      }
    }
    .tab-nav-link {
      border: none;
      background: none;
      padding: 3.5rem 2rem;
      width: 100%;
    }
  }

  &-content {
    display: flex;
    padding: 2rem;
    background-color: $white;
    box-shadow: 0 -2px 5px 0 rgba(0, 0, 0, 0.02), 0 18px 17px 0 rgba(0, 0, 0, 0.08),
      0 2px 4px 0 rgba(0, 0, 0, 0.08);
    border-top: solid 4px #003b5c;

    @include desktop-up {
      padding: 3rem;
    }

    @include large-desktop-up {
      padding: 4rem 5rem;
    }

    > div {
      width: 100%;
    }

    &:deep() {
      p.disclaimer {
        font-size: 1.4rem !important;
      }
    }
  }

  &-mobile {
    @include tablet-up {
      display: none;
    }

    &__title {
      position: relative;
      padding: 2.2rem 5.5rem 2.2rem 2rem;
      margin-bottom: 0.4rem;
      background: linear-gradient(
        169deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 59, 92, 1) 20%,
        rgba(0, 134, 191, 1) 100%
      );
      box-shadow: 0 -2px 5px 0 rgba(0, 0, 0, 0.02), 0 18px 17px 0 rgba(0, 0, 0, 0.08),
        0 2px 4px 0 rgba(0, 0, 0, 0.08);
      @include font-styles('Arial', 1.8rem, 300, normal, 2.4rem, 0, $white);

      &:before {
        position: absolute;
        font-weight: 700;
        font-size: 2rem;
        content: '∟';
        transform: translate(0, -50%) rotate(-45deg);
        color: $white;
        right: 2rem;
        top: 50%;
        pointer-events: none;
        transition: all 0.5s;
      }
    }

    &__content {
      display: none;
      padding: 2rem 2rem 1rem 2rem;
      box-shadow: 0 -2px 5px 0 rgba(0, 0, 0, 0.02), 0 18px 17px 0 rgba(0, 0, 0, 0.08),
        0 2px 4px 0 rgba(0, 0, 0, 0.08);

      @include tablet-up {
        padding: 2rem;
      }
    }

    .active {
      .c-tabs-mobile__title {
        &:before {
          transform: translate(0, -50%) rotate(135deg);
        }
      }

      .c-tabs-mobile__content {
        display: block;
      }
    }
  }
}

.c-tabs-mobile {
  margin-bottom: 4rem;

  &__content {
    ul {
      li {
        padding-bottom: 1.5rem;
      }
    }
  }
}
</style>
